<template>
  <div
    class="bg-white dark:bg-gray-800 rounded-lg h-auto flex flex-col items-center justify-center w-full p-6 relative"
  >
    <div class="flex justify-between items-center mb-6 w-full">
      <h3 class="text-xl font-bold dark:text-gray-200">Fund Wallet</h3>
      <button
        @click="$emit('close')"
        class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>

    <form @submit.prevent="fundWallet" class="space-y-6 w-full">
      <div>
        <label class="block mb-2 text-sm font-medium dark:text-gray-300"
          >Amount (₦)</label
        >
        <input
          v-model="amount"
          type="number"
          min="100"
          inputmode="decimal"
          step="100"
          :class="{
            'no-spinner peer h-full w-full rounded-lg border border-blue-gray-200 dark:border-gray-600 border-t-transparent !border-t-blue-gray-200 dark:!border-t-gray-600 bg-transparent px-3 py-2.5 text-sm font-normal text-blue-gray-700 dark:text-gray-200 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 dark:placeholder-shown:border-gray-600 placeholder-shown:border-t-blue-gray-200 dark:placeholder-shown:border-t-gray-600 focus:border-2 focus:border-gray-900 dark:focus:border-gray-300 focus:border-t-transparent focus:!border-t-gray-900 dark:focus:!border-t-gray-300 focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50 dark:disabled:bg-gray-700 !pl-9': true,
            'outline-2 !outline-red-800 dark:!outline-red-500 !bg-red-800/10 dark:!bg-red-500/10 !border-red-800 dark:!border-red-500 !text-red-600 dark:!text-red-400 focus:!border-t-red-800 dark:focus:!border-t-red-500 !border-t-red-800 dark:!border-t-red-500 placeholder-red-800/60 dark:placeholder-red-500/60':
              amountError,
          }"
          placeholder="1000"
          required
          @input="calculateBonus"
        />
      </div>

      <!-- Funding Bonus Display -->
      <div
        v-if="bonus > 0"
        class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4"
      >
        <div class="flex justify-between items-center">
          <div>
            <p class="font-medium text-green-700 dark:text-green-300">
              Bonus Credit
            </p>
            <p class="text-sm text-green-600 dark:text-green-400">
              You'll get extra ₦{{ bonus }} for funding this amount!
            </p>
          </div>
          <div
            class="bg-green-100 dark:bg-green-800/50 text-green-700 dark:text-green-300 px-3 py-1 rounded-full text-sm font-medium"
          >
            +₦{{ bonus }}
          </div>
        </div>
      </div>

      <!-- Funding Tiers -->
      <div>
        <label class="block mb-2 text-sm font-medium dark:text-gray-300">
          Funding Bonus Tiers
        </label>
        <div class="space-y-3">
          <div class="grid grid-cols-3 gap-2 text-center">
            <div
              @click="amount = 3000"
              :class="{
                'bg-main-100 dark:bg-sub-900/30 border-main-300 dark:border-main-700':
                  amount >= 3000 && amount < 10000,
                'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600':
                  amount < 3000,
              }"
              class="border rounded-lg p-3 cursor-pointer hover:bg-main-50 dark:hover:bg-sub-900/20 transition"
            >
              <p class="font-bold dark:text-gray-200">₦3,000</p>
              <p class="text-xs text-green-500 dark:text-green-400">+₦500</p>
            </div>
            <div
              @click="amount = 10000"
              :class="{
                'bg-main-100 dark:bg-sub-900/30 border-main-300 dark:border-main-700':
                  amount >= 10000 && amount < 50000,
                'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600':
                  amount < 10000,
              }"
              class="border rounded-lg p-3 cursor-pointer hover:bg-main-50 dark:hover:bg-sub-900/20 transition"
            >
              <p class="font-bold dark:text-gray-200">₦10,000</p>
              <p class="text-xs text-green-500 dark:text-green-400">+₦1,000</p>
            </div>
            <div
              @click="amount = 50000"
              :class="{
                'bg-main-100 dark:bg-sub-900/30 border-main-300 dark:border-main-700':
                  amount >= 50000,
                'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600':
                  amount < 50000,
              }"
              class="border rounded-lg p-3 cursor-pointer hover:bg-main-50 dark:hover:bg-sub-900/20 transition"
            >
              <p class="font-bold dark:text-gray-200">₦50,000</p>
              <p class="text-xs text-green-500 dark:text-green-400">+₦6,000</p>
            </div>
          </div>
        </div>
      </div>

      <div class="pt-4">
        <button
          type="submit"
          :disabled="wait"
          class="w-full bg-main-500 hover:bg-main-600 dark:bg-sub-600 dark:hover:bg-sub-700 text-white py-3 px-4 rounded-lg transition flex items-center justify-center gap-2"
        >
          <i class="fas fa-wallet"></i>
          Fund ₦{{ amount || "0" }}
          <span v-if="bonus > 0" class="ml-1 text-green-200"
            >(+₦{{ bonus }})</span
          >
          <span v-if="wait" class="ml-2 animate-spin"
            ><i class="fas fa-spinner"></i
          ></span>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
export default {
  props: {
    suggestedAmount: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      amount: "",
      bonus: 0,
      wait: false,
      amountError: false,
    };
  },
  methods: {
    calculateBonus() {
      const amount = parseFloat(this.amount) || 0;
      if (amount >= 50000) {
        this.bonus = 6000;
      } else if (amount >= 10000) {
        this.bonus = 1000;
      } else if (amount >= 3000) {
        this.bonus = 200;
      } else {
        this.bonus = 0;
      }
    },
    async fundWallet() {
      this.wait = true;
      try {
        // Call Vuex action
        const result = await this.$store.dispatch("fundWallet", {
          amount: this.amount,
          bonus: this.bonus,
        });

        if (result?.success) {
          this.$toast.success("Wallet funded successfully!");
          this.$emit("funded", result);
          this.$emit("close");
        } else {
          this.$toast.error(result?.error || "Funding failed.");
        }
      } catch (error) {
        console.error("Funding error:", error);
      } finally {
        this.wait = false;
      }
    },
  },
  mounted() {
    if (this.suggestedAmount > 0) {
      this.amount = this.suggestedAmount;
    }
  },
  watch: {
    amount() {
      this.calculateBonus();
    },
  },
};
</script>
